// OnboardingParts.tsx — the pieces the Onboarding list and a person's record share (12 Oct 2026, the makeover):
// the four checks as chips and as cards, what the wallet holds, the trade, the chat button in its app's own
// colour, and how a read says when it was read.
//
// The operator, 9 Oct 2026: "size of the fonts, using cards, colors where applicable, reads as sections like SoF
// Passed, KYC passed, Residency/nationality passed/flagged/pennding, tg card with Tg blue color"; 12 Oct: "wallet
// funds should also be listed on that screen itself".
//
// COLOUR ONLY MEANS SOMETHING: green passed, amber pending, red flagged — always beside the word that says it; the
// coin on sale green when the wallet covers the claim and red when it is short; Telegram blue and WhatsApp green on
// the button that opens that app. Nothing else is coloured.
//
// LINES THAT READ CLEAN: a reason is two lines on a list row (three on a record's card), cut where it is measured to
// be cut, opened in place by a tap; its short parts ("risk low (12)", "Made-up Mixer") never split; an amount is one
// token; no line starts with a stray "·".
import { Fragment, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { Globe, MessageCircle, Send, ArrowUpRight } from 'lucide-react';
import { api, type ChatLink, type LeadChannel } from '../lib/api';
import { ApiError, errorWords } from '../lib/errors';
import { fmtAmount, fmtStamp } from '../lib/format';
import { foldAsset } from '../lib/naming';
import {
  CHECKS, VERDICT, ago, waitingWords,
  type Assessment, type Check, type CheckKey, type CheckStatus, type Claim, type Facts, type WaitingOn, type WalletLine,
} from '../lib/onboarding';
import { OfflineLine, StateLine } from './kit';
import { offlineFailure } from '../lib/read-state';
import { uiV2 } from '../lib/ui-v2';

/** A layout effect in the browser; nothing on the server (the tests render this page to a string). */
const useClientLayoutEffect = typeof document !== 'undefined' ? useLayoutEffect : useEffect;

// ── a read, and when it was read ─────────────────────────────────────────────

/** A read older than this says so (rule 2). */
export const STALE_MS = 10 * 60_000;
/** The lists — and an open record — are read again this often while they are on screen. */
export const READ_EVERY_MS = 60_000;

/** An age in words (lib/onboarding.ts, where the wallet's live line counts its age too). */
export { ago };

/** A read: what is shown, when it was read, whether that is fresh. */
export interface Read<T> { d: T | undefined; readMs: number; fresh: boolean; readWords: string | null }

/** A list's read. A failed read never shows an empty list — the last one read stays only when it holds something,
 *  and is then said to be the earlier one (rule 4). */
export function readOf<T extends { fetched_at: string }>(q: UseQueryResult<T>, holds: (d: T) => boolean): Read<T> {
  const d = q.data && !(q.isError && !holds(q.data)) ? q.data : undefined;
  const readMs = d ? Date.parse(d.fetched_at) : NaN;
  const age = Date.now() - readMs;
  const fresh = Number.isFinite(readMs) && age <= STALE_MS;
  const readWords = !d ? null : Number.isFinite(readMs) ? `${fmtStamp(d.fetched_at)} · ${ago(age)}` : 'at a time not known';
  return { d, readMs, fresh, readWords };
}

/** The search's and the team's requests' own refusals (bff/src/routes/leads.ts): their sentences are the route's. */
const OWN_REFUSALS = new Set(['bad_query', 'bad_items', 'note_needed', 'note_too_long', 'not_needed']);

/** What failed, in words. The house's sentence for a 502 it has no case for is "the books did not answer … Docker";
 *  here the Books answered — the onboarding worker did not, and the route says which way. */
export function failWords(e: unknown): string {
  const said = e instanceof ApiError && typeof e.body.reason === 'string' && e.body.reason
    && (e.code.startsWith('leads_') || e.code === 'no_such_lead' || e.code === 'bad_ref' || e.code === 'bad_admin' || Object.hasOwn(CHAT_REFUSAL_WORD, e.code)
      || OWN_REFUSALS.has(e.code))
    ? e.body.reason
    : errorWords(e);
  const t = said.trim().replace(/\.$/, '');
  return /^[A-Z][a-z]/.test(t) ? `${t.charAt(0).toLowerCase()}${t.slice(1)}` : t;
}

// ── a failed read, as ONE line (the design-system sweep, 22 Oct 2026) ────────
// The Home, Money, Trades, Legacy-history, Names, Onboarding and Sign-in screens import these from here: the sweep's one
// line for "could not be read" — a headline, the cause behind it when short, else behind Details — lives with the file that
// already owned the wording of the onboarding reads. A candidate for components/kit.tsx.

/** A server's sentence split at its FIRST sentence — the building block of `failLine` and `refusalLine`, which decide what
 *  rides the line and what goes behind the tap. A sentence ends at . ! or ? followed by a capital; text with no such end is
 *  one sentence and stays whole. */
export function sayFirst(sentence: string): { first: string; rest: string | null } {
  const t = sentence.trim();
  const m = /^([\s\S]+?[.!?])\s+(?=[A-Z])/.exec(t);
  if (!m) return { first: t, rest: null };
  return { first: m[1]!, rest: t.slice(m[0].length).trim() || null };
}

/** A state is ONE line of ten words or fewer (docs/DESIGN-SYSTEM.md §3). The words are counted as they PRINT — a figure and a
 *  timestamp ("16:00 · 22 min ago" is four) are words, a dash is not — because the word gate sees a dynamic part as one. */
const STATE_WORDS = 10;
/** The most words of the server's own sentence that ride a headline; longer, and the whole sentence waits behind Details. */
const CAUSE_WORDS = 6;
const wordCount = (s: string): number => s.split(/\s+/).filter((w) => /[\p{L}\p{N}]/u.test(w)).length;

// A sentence the route cut at 300 characters ends in "…": that is its end — no full stop after it.
const closed = (s: string): string => (/[.!?…]$/.test(s) ? s : `${s}.`);
const capital = (s: string): string => (/^[a-z]/.test(s) ? `${s.charAt(0).toUpperCase()}${s.slice(1)}` : s);
/** After a dash a sentence starts in lower case ("— the ledger did not answer"). */
const afterDash = (s: string): string => (/^[A-Z][a-z]/.test(s) ? `${s.charAt(0).toLowerCase()}${s.slice(1)}` : s);

/** A failed read as ONE line: its HEADLINE ("Couldn't read the chain") and, after a dash, the server's first sentence only
 *  when that is short — at most 6 words, and 10 with the headline. A longer sentence is NOT on the line: the whole of it
 *  (capitalised, closed) is `more`, behind Details. When the first sentence rides the line, the rest of the sentence is
 *  `more`. The server's wording is never cut, only moved behind the tap (Astra, 22 Oct 2026: a long single sentence used to
 *  stay whole on the line, a 12-word chain warning among them). */
export function failLine(headline: string, sentence: string): { line: string; more?: string } {
  const full = closed(capital(sentence.trim()));
  const { first, rest } = sayFirst(full);
  if (wordCount(first) === 0) return { line: `${headline}.` };
  if (wordCount(first) <= CAUSE_WORDS && wordCount(headline) + wordCount(first) <= STATE_WORDS) {
    return { line: `${headline} — ${afterDash(first)}`, ...(rest ? { more: rest } : {}) };
  }
  return { line: `${headline}.`, more: full };
}

/** A failed read's sentence for ONE line, in the onboarding reads' own voice: `failWords` (lower case, no last full stop)
 *  under the headline it is given — see `failLine`. */
export function failSaid(headline: string, e: unknown): { line: string; more?: string } {
  return failLine(headline, failWords(e));
}

/** A sentence for behind the tap: capitalised and closed. */
export const sentenceOf = (s: string): string => closed(capital(s.trim()));
/** A failed read's whole sentence, for behind the tap: `failWords`, capitalised and closed. */
export const failMore = (e: unknown): string => sentenceOf(failWords(e));

/** What a failed REFRESH says when the read it keeps is an empty one: the cached answer was none — said as the LAST read's, never
 *  as today's ("Nothing in this period." beside a failure is a quiet day claimed over a read that did not come). */
export const LAST_READ_NONE = 'the last read showed none here';

/** A failed read as ONE line — what could not be read and, when it is short, why — the whole of a longer sentence behind
 *  "Details" and, when the screen can read it again, "Try again" (§5: "one line, amber or red by severity, what failed + what
 *  to do"). `says` puts the screen's own words after the dash ("showing the last good read") and the whole server sentence
 *  behind the tap. Never drawn like an empty read: it is amber and it says what failed. */
export function ReadFailed({ what, error, says, retry, className = '' }: {
  what: string; error: unknown; says?: string; retry?: () => void; className?: string;
}) {
  // The rebuilt console (uiV2, switched off today): a first read that failed only for want of a connection is waiting, not a
  // failure (lib/read-state.ts offlineFailure) — its shell reads again when the connection is back. Today's console is untouched.
  if (uiV2(1) && offlineFailure(error)) return <OfflineLine what={what} kept={!!says && says !== LAST_READ_NONE} className={className} />;
  // A failed read has an error; asked for with none, it says what could not be read and nothing else — never errorWords(null), "null".
  const sentence = error == null ? '' : errorWords(error);
  const headline = `Couldn't read ${what}`;
  const { line, more } = says ? { line: `${headline} — ${says}.`, more: sentence ? closed(sentence) : undefined } : failLine(headline, sentence);
  return (
    <StateLine
      tone="warn"
      className={className}
      more={more}
      action={retry && <button type="button" className="btn-link text-meta" onClick={retry}>Try again</button>}
    >
      {line}
    </StateLine>
  );
}

/** The refusals whose own sentence is a paragraph: their one-line headline says what is wrong with what was sent, and the whole
 *  sentence — every accepted form in it — is behind Details. Keyed by the BFF's error code. A candidate for lib/errors.ts
 *  `explain()`, where the words of every other refusal live. */
const REFUSAL_HEADLINE: Record<string, string> = {
  // bff/src/routes/migration.ts: a Mark "Entered" whose reference the system never minted ("Posted", "Done"). Its detail is a
  // 29-word sentence with no full stop inside it, and a colon inside its own quotation.
  deal_id_not_a_reference: 'That is not a reference this system mints.',
};

/** A refusal's code, spoken: "deal_id_not_a_reference" → "Deal id not a reference." */
const spoken = (code: string): string => closed(capital(code.replace(/[_-]+/g, ' ').trim()));

/** A clause that GRANTS something — "…is open, but …", "…is fine — however …" — is the opposite of the refusal: the line never takes one. */
const CONCESSION = /^\s*(?:but|though|although|however|yet|except|unless|whereas|while)\b/i;

/** A refusal's first CLAUSE, for when its first sentence is over the limit: the text before a dash, a colon, a semicolon or an
 *  opening parenthesis; else before the first comma that leaves three to ten words. Null when there is none to give, or when
 *  what follows it takes the clause back (`CONCESSION`) — a headline must never say what the refusal denies. */
function firstClause(first: string): string | null {
  const fits = (c: string): boolean => wordCount(c) >= 3 && wordCount(c) <= STATE_WORDS;
  const strong = /^(.+?)(?:\s[—–]\s|:\s|;\s|\s\()/s.exec(first);
  if (strong) {
    if (CONCESSION.test(first.slice(strong[0].length))) return null;
    if (fits(strong[1]!)) return strong[1]!;
  }
  for (let i = first.indexOf(','); i >= 0; i = first.indexOf(',', i + 1)) {
    // A comma between two digits is a thousands separator ("1,008,212.50"), not a clause boundary — cutting there prints a false amount.
    if (/\d/.test(first.charAt(i - 1)) && /\d/.test(first.charAt(i + 1))) continue;
    const clause = first.slice(0, i).trim();
    if (fits(clause)) return CONCESSION.test(first.slice(i + 1)) ? null : clause;
    if (wordCount(clause) > STATE_WORDS) break;
  }
  return null;
}

/** A refusal's sentence for ONE line. Its REASON stays on the line — it is what stops the wrong second click — so there is no
 *  headline to fold it behind: a first sentence of ten words or fewer is the line (its second sentence behind Details). Over
 *  ten, there is no exception for eleven or twelve: the line is a HEADLINE of at most ten words — the one the screen knows for
 *  that code, else the first clause, else the refusal's code spoken ("Refused." with none) — and the WHOLE sentence is what
 *  Details holds (Astra, round 2: a 29-word refusal had no Details at all; round 2b: eleven and twelve words stayed whole). */
export function refusalLine(sentence: string, code?: string): { line: string; more?: string } {
  const full = closed(sentence.trim());
  const { first, rest } = sayFirst(full);
  if (wordCount(first) <= STATE_WORDS) return { line: capital(first), ...(rest ? { more: rest } : {}) };
  const whole = capital(full);
  const known = code ? REFUSAL_HEADLINE[code] : undefined;
  if (known) return { line: known, more: whole };
  const clause = firstClause(first);
  if (clause) return { line: closed(capital(clause)), more: whole };
  return { line: code ? spoken(code) : 'Refused.', more: whole };
}

/** An action the books refused (a write that failed), as ONE red line — see `refusalLine`. */
export function Refused({ error, className = '' }: { error: unknown; className?: string }) {
  const { line, more } = refusalLine(errorWords(error), error instanceof ApiError ? error.code : undefined);
  return <StateLine tone="bad" className={className} more={more}>{line}</StateLine>;
}

// ── the four checks ──────────────────────────────────────────────────────────

/** Green passed, amber pending, red flagged: the pill, the card's left edge and tint, and the word's colour. */
export const LOOK: Record<CheckStatus, { pill: string; edge: string; tint: string; card: string; text: string }> = {
  passed: { pill: 'bg-pos-bg text-pos', edge: 'border-l-pos', tint: 'bg-pos/[0.06]', card: 'bg-pos-bg', text: 'text-pos' },
  pending: { pill: 'bg-warn-bg text-warn', edge: 'border-l-warn', tint: 'bg-warn/[0.06]', card: 'bg-warn-bg', text: 'text-warn' },
  flagged: { pill: 'bg-neg-bg text-neg', edge: 'border-l-neg', tint: 'bg-neg/[0.07]', card: 'bg-neg-bg', text: 'text-neg' },
};

/** A verdict in a word, tinted: "Pending" — or, where two stand side by side, whose: "Wallet · Pending". */
export function StatusPill({ s, of }: { s: CheckStatus; of?: string }) {
  return (
    <span className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-meta font-bold ${LOOK[s].pill}`} data-verdict={s}>
      {of && <span className="mr-1 font-semibold opacity-80">{of} ·</span>}{VERDICT[s]}
    </span>
  );
}

/** A day and time as the screen writes them ("28 Sep, 13:01", or "28 Sep") at the end of a part. */
const STAMP_AT_END = /^(.*?)(\d{1,2} [A-Z][a-z]{2}(?:, \d{2}:\d{2})?)$/;

/** A reason's parts between " · " and " — ": a short one ("risk low (12)", "Made-up Mixer") kept whole on its line,
 *  and the dot or dash kept at the end of the line before — never "risk / low (12)" or "Made- / up Mixer", never a
 *  line that starts with "·". Sixteen characters fit a check's narrowest column, so a whole part never overflows.
 *  A longer part that ends in a date keeps the DATE whole — "sent 28 Sep, 13:01" breaks before "28", never between
 *  "28" and "Sep," (the 21 Oct 2026 phone audit of a request's trail). */
export function Parts({ text }: { text: string }) {
  return (
    <>
      {text.split(/( · | — )/).map((x, i) => {
        if (i % 2 === 1) return <Fragment key={i}>{` ${x.trim()} `}</Fragment>;
        if (x.length <= 16) return <Fragment key={i}><span className="whitespace-nowrap">{x}</span></Fragment>;
        const stamp = STAMP_AT_END.exec(x);
        return <Fragment key={i}>{stamp ? <>{stamp[1]}<span className="whitespace-nowrap">{stamp[2]}</span></> : x}</Fragment>;
      })}
    </>
  );
}

/** A reason at body size, two lines at most on a list row (three on a record's card) — when the lines cut it short
 *  (measured, not guessed), it opens in place on a tap (a tooltip does nothing on a phone) and closes on another. The
 *  measured span is the same element for the reason's whole life, so a late measure never reads a detached copy. */
export function Reason({ text, lines = 2, size = 'text-body' }: { text: string | null; lines?: 2 | 3; size?: string }) {
  const [open, setOpen] = useState(false);
  const [cut, setCut] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useClientLayoutEffect(() => {
    const el = ref.current;
    if (!el || open) return;
    const measure = () => { if (el.isConnected) setCut(el.scrollHeight > el.clientHeight + 1); };
    measure();
    // Measured again once the page has painted and its fonts are in: a first measure can come before either.
    const frame = requestAnimationFrame(measure);
    void document.fonts?.ready.then(measure);
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => { cancelAnimationFrame(frame); ro.disconnect(); };
  }, [open, text, lines]);
  if (!text) return null;
  const toggles = cut || open;
  return (
    // The whole reason takes the tap when it is cut; the "more" button is the control a keyboard or reader finds.
    <div onClick={toggles ? () => setOpen(!open) : undefined} title={toggles && !open ? text : undefined}
      className={`mt-1 text-pretty ${size} leading-snug ${toggles ? 'cursor-pointer' : ''}`} data-why={size === 'text-body' ? undefined : ''}>
      <span ref={ref} className={open ? 'block' : lines === 3 ? 'line-clamp-3' : 'line-clamp-2'} data-reason="">
        <Parts text={text} />
      </span>
      {toggles && <button type="button" aria-expanded={open} className="text-body text-tg-link">{open ? 'less' : 'more'}</button>}
    </div>
  );
}

/** A check on a list row: its short name, its verdict, and its reason — two lines, the rest on a tap. Under it, room
 *  for one short line about a request to the customer for this check (a later plan: "Asked 12 Oct · waiting for them to
 *  write"); nothing fills it yet. */
function CheckChip({ name, full, c, request = null }: { name: string; full: string; c: Check; request?: string | null }) {
  const l = LOOK[c.status];
  return (
    <div className={`min-w-0 rounded-lg border-l-[3px] ${l.edge} ${l.tint} px-2.5 py-2`} data-check={name}>
      <div className="flex flex-wrap items-center justify-between gap-x-1.5 gap-y-1" title={full}>
        <span className="whitespace-nowrap text-label font-semibold uppercase tracking-wide text-tg-hint">{name}</span>
        <StatusPill s={c.status} />
      </div>
      <Reason text={c.reason} />
      {request && <p className="mt-1 truncate text-meta text-tg-hint" title={request} data-request="">{request}</p>}
    </div>
  );
}
/** A check the BFF could not read: no verdict, only that it is not known. */
function UnknownChip({ name, full }: { name: string; full: string }) {
  return (
    <div className="min-w-0 rounded-lg border-l-[3px] border-l-stroke px-3 py-2" data-check={name} title={full}>
      <span className="whitespace-nowrap text-label font-semibold uppercase tracking-wide text-tg-hint">{name}</span>
      <p className="mt-1 text-body text-tg-hint">Not known</p>
    </div>
  );
}
/** A row's four checks, side by side from xl (each card's chips lining up with the next card's), two by two below
 *  it. None from a worker older than them: nothing — never a verdict made up for what was not sent. */
export function CheckChips({ a }: { a: Assessment | null }) {
  if (!a) return null;
  return (
    <div className="grid grid-cols-2 gap-2 xl:contents" aria-label="The four checks">
      {CHECKS.map((k) => {
        const c = a[k.key];
        return c ? <CheckChip key={k.key} name={k.short} full={k.name} c={c} /> : <UnknownChip key={k.key} name={k.short} full={k.name} />;
      })}
    </div>
  );
}

/** A card's look without the house .card class: at lg the house re-colours every .card border (styles.css), which
 *  would take the colour off a seat's left edge — and that colour is what the edge is for. */
export const SEAT = 'rounded-xl border border-stroke bg-tg-section-bg';

/** The record's four checks at a glance: one card each, its edge and tint in its verdict's colour, its name taking you
 *  to its section, its reason in three lines — and, with a worker that takes the team's requests (`ask`), "Ask the
 *  customer" under it (12 Oct 2026, the plan's phase 2), which opens the form for that check (`asking`). Under the
 *  cards, what goes with them (`children`): what is still missing, and the form. The edge sits on the seat and the
 *  tint on a layer inside: two background classes on one element are settled by the stylesheet's order, which let one
 *  tint show and hid the others. */
export function StatusCards({ a, go, ask, asking = null, children }: {
  a: Assessment | null; go: (section: 'funds' | 'kyc' | 'residency') => void;
  ask?: (k: CheckKey) => void; asking?: CheckKey | null; children?: ReactNode;
}) {
  const head = <h3 className="text-label font-semibold uppercase tracking-wide text-tg-hint">Verifications</h3>;
  return (
    <section className="space-y-2" aria-label="Verifications">
      {head}
      {!a ? (
        <p className="text-body text-tg-hint">Checks not available yet — next bot update.</p>
      ) : (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" aria-label="The four checks">
          {CHECKS.map((k) => {
            const c = a[k.key];
            // Source of funds and Wallet are one section, "Wallet & source of funds": both cards open it.
            const to = k.key === 'wallet' || k.key === 'funds' ? 'funds' : k.key;
            const verdict = c ? VERDICT[c.status] : 'Not known';
            return (
              <div key={k.key} className={`${SEAT} overflow-hidden border-l-4 ${c ? LOOK[c.status].edge : 'border-l-stroke'}`} data-check={k.short} data-verdict={c?.status ?? 'unknown'}>
                <div className={`flex h-full flex-col px-4 py-3 ${c ? LOOK[c.status].card : ''}`}>
                  <button type="button" className="block w-full text-left" aria-label={`${k.name}: ${verdict} — open its section`} onClick={() => go(to)}>
                    <span className="block text-label font-semibold uppercase tracking-wide text-tg-hint">{k.name}</span>
                    <span className={`mt-1 block text-title font-bold ${c ? LOOK[c.status].text : 'text-tg-hint'}`}>{verdict}</span>
                  </button>
                  {c && <Reason text={c.reason} lines={3} />}
                  {ask && (
                    <button type="button" className={`mt-auto self-start pt-2 text-left text-body text-tg-link hover:underline ${asking === k.key ? 'font-semibold underline' : 'font-medium'}`}
                      aria-expanded={asking === k.key} aria-label={`Ask the customer — ${k.name}`} data-ask={k.key}
                      onClick={() => ask(k.key)}>
                      Ask the customer
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {children}
    </section>
  );
}

/** "Waiting on them" — what is missing, the reminders sent, until when the bot can write (lib/onboarding.ts
 *  waitingWords) — plain while the bot can still ask, amber with what to do when it cannot. `items` false leaves
 *  the item names out: the record says them once, in its "Still missing" chips. */
export function WaitingLine({ w, channel, readMs, className = '', items = true }: { w: WaitingOn; channel: LeadChannel | null; readMs: number; className?: string; items?: boolean }) {
  const said = waitingWords(w, channel, readMs, items);
  return (
    <p className={`text-body leading-snug ${said.tone === 'warn' ? 'font-medium text-warn' : 'text-tg-text'} ${className}`} data-waiting={said.tone}>
      <Parts text={said.text} />
    </p>
  );
}

// ── the trade, and what the wallet holds ─────────────────────────────────────

const SIDE_WORD = { buy: 'Buy', sell: 'Sell' } as const;
/** The intake's codes for how a sale is paid, as words. */
export const PAYOUT_WORD: Record<string, string> = { bank_transfer: 'Bank transfer', manager_cheque: "Manager's cheque", cash: 'Cash', developer: 'Developer payment' };
/** The AED equivalent of the first trade, when no amount was read. */
const SIZE_WORD: Record<Claim['sizeBand'], string | null> = {
  under_50k: 'under 50k AED', '50k_200k': '50–200k AED', '200k_1m': '200k–1M AED', over_1m: 'over 1M AED', unclear: 'size unclear', unknown: null,
};

/** The amount in AED as one token — "165,513 AED", "50,000–80,000 AED", "up to 300,000 AED" — or null with none read. */
export function amountWords(a: Claim['amountAed']): string | null {
  if (!a) return null;
  // An estimate of the trade, in whole dirhams: the fils of a converted figure are not the operator's business.
  const low = fmtAmount(Math.round(a.low), 'AED');
  const high = fmtAmount(Math.round(a.high), 'AED');
  // The unit is glued to the last figure with a no-break space: a range may wrap after its dash, a figure never splits from its
  // unit and never mid-number (the comma is not a break).
  return `${a.upTo ? 'up to ' : ''}${low === high ? low : `${low}–${high}`}\u00A0AED`;
}
/** How a sale is paid: a manager's cheque or a developer by the kind of trade, else the answer; a buy says nothing. */
export function payoutWords(c: Claim | null, f: Pick<Facts, 'payout'> | null): string | null {
  if (c?.type === 'mc') return "Manager's cheque";
  if (c?.type === 'developer') return 'Developer payment';
  if (c?.side !== 'sell' || !f?.payout) return null;
  return Object.hasOwn(PAYOUT_WORD, f.payout) ? PAYOUT_WORD[f.payout] ?? f.payout : f.payout.replace(/_/g, ' ');
}

/** The trade, big: the side and coin over the amount — "SELL USDT" / "165,513 AED" — then what it is in USDT when the
 *  worker said, and how they are paid, each on a line of its own. With no amount read: their own words, else the
 *  size's band, said plainly and smaller — never passed off as a figure. */
export function TradeBig({ c, f, usdt = null, size = 'list' }: {
  c: Claim | null; f: Pick<Facts, 'size' | 'payout'> | null; usdt?: { low: number; high: number } | null; size?: 'list' | 'record';
}) {
  if (!c || (!c.side && !c.amountAed && !f?.size && !SIZE_WORD[c.sizeBand])) {
    return <div className="min-w-0" data-trade=""><p className="text-body text-tg-hint">No trade on record</p></div>;
  }
  const head = [c.side ? SIDE_WORD[c.side] : null, c.asset].filter(Boolean).join(' ') || 'Buy or sell not said';
  const amount = amountWords(c.amountAed);
  const said = !amount ? f?.size ?? SIZE_WORD[c.sizeBand] : null;
  const payout = payoutWords(c, f);
  const stable = foldAsset(c.asset);
  const coin = stable === 'USDC' ? 'USDC' : 'USDT';
  const u = usdt && (stable === 'USDT' || stable === 'USDC')
    ? `≈ ${fmtAmount(Math.round(usdt.low), undefined, 0)}${usdt.high !== usdt.low ? `–${fmtAmount(Math.round(usdt.high), undefined, 0)}` : ''} ${coin}`
    : null;
  return (
    <div className="min-w-0" data-trade="">
      <p className="text-body font-bold uppercase tracking-[0.08em] text-tg-hint">{head}</p>
      {amount
        ? <p className={`${size === 'record' ? 'text-figure' : 'text-title'} font-semibold leading-tight tabular-nums`}>{amount}</p>
        : <p className="text-body leading-snug">{said ? <>{said}</> : <span className="text-tg-hint">No amount yet</span>}</p>}
      {u && <p className="mt-0.5 whitespace-nowrap text-body tabular-nums text-tg-hint">{u}</p>}
      {payout && <p className="text-body leading-snug text-tg-hint">{payout}</p>}
    </div>
  );
}

/** What the wallet holds, on one line: "Wallet: 30,000.5 USDT · 1,204.25 TRX" — the coin on sale first, green when it
 *  covers the claim and red when short; or why there is nothing to list, in words. Read live (lib/onboarding.ts
 *  liveLine), when it was read after it. Each coin and its amount is one token, never split. */
export function WalletLineView({ line, label = true }: { line: WalletLine; label?: boolean }) {
  const tone = (t: 'pos' | 'neg' | 'muted' | null) => (t === 'pos' ? 'font-semibold text-pos' : t === 'neg' ? 'font-semibold text-neg' : t === 'muted' ? 'text-tg-hint' : '');
  return (
    <p className="text-body leading-snug tabular-nums" data-wallet="">
      {label && <span className="text-tg-hint">Wallet: </span>}
      {line.kind === 'held'
        ? line.parts.map((p, i) => (
            <Fragment key={p.asset}>
              {i > 0 && <span className="text-tg-hint">{' · '}</span>}
              <span className={`whitespace-nowrap ${tone(p.tone)}`}>{fmtAmount(p.amount, p.asset)} {p.asset}</span>
            </Fragment>
          ))
        : <span className={tone(line.tone)}>{line.text}</span>}
      {/* Read live: when — "· 3 min ago", its dot held to the coin before it, as the coins' are — or, on a line of its own
          in amber, that the chain did not answer and when the reading shown is from. */}
      {line.when && (line.when.tone === 'warn'
        ? <span className="block text-body text-warn" data-live="warn"><Parts text={line.when.text} /></span>
        : (
          <>
            <span className="text-tg-hint">{'\u00a0· '}</span>
            <span className="whitespace-nowrap text-body text-tg-hint" data-live="muted">{line.when.text}</span>
          </>
        ))}
    </p>
  );
}

// ── the chat button ──────────────────────────────────────────────────────────

/** The Mini App's own openers (Bot API 6.1+) — absent in a browser, and on old clients. */
type Opener = { initData?: string; openTelegramLink?: (url: string) => void; openLink?: (url: string) => void };

/** Opens a chat link where the operator is. Inside the Mini App, through Telegram's own openers: a t.me link in
 *  Telegram itself, a wa.me link out to WhatsApp. In a browser, in a new tab that gets no hold on this one. Answers
 *  which way it went. */
export function openChat(url: string, w: Window = window): 'telegram' | 'link' | 'tab' {
  const app = (w as unknown as { Telegram?: { WebApp?: Opener } }).Telegram?.WebApp;
  if (app?.initData) {
    if (url.startsWith('https://t.me/') && typeof app.openTelegramLink === 'function') {
      app.openTelegramLink(url);
      return 'telegram';
    }
    if (typeof app.openLink === 'function') {
      app.openLink(url);
      return 'link';
    }
  }
  w.open(url, '_blank', 'noopener');
  return 'tab';
}

/** What the chat-link route's refusals mean to the operator, in the words the brief gave them. */
export const CHAT_REFUSAL_WORD: Record<string, string> = {
  privacy: 'Their Telegram privacy settings block this — reply through the bot',
  not_sent: "Telegram didn't take it — try again",
  no_way_to_reach: 'No way to reach them outside the bot',
  no_such_person: 'They are no longer on record',
};

/** The line under a chat button the bot was asked for: where the button went — or, when it answered with their own
 *  link (they have a username now), that their chat was opened — or why nothing was sent. */
export function chatLinkNote(r: ChatLink | null, err?: unknown): { text: string; tone: 'plain' | 'warn' } {
  if (r) {
    if (r.via === 'bot_dm') return { text: 'Sent you the button in your chat with the bot', tone: 'plain' };
    if (r.via === 'ops_group') return { text: 'Posted the button in the ops group', tone: 'plain' };
    return { text: 'Opened their chat', tone: 'plain' };
  }
  const code = err instanceof ApiError ? err.code : null;
  if (code && Object.hasOwn(CHAT_REFUSAL_WORD, code)) return { text: CHAT_REFUSAL_WORD[code] ?? code, tone: 'warn' };
  return { text: `Couldn't open their chat — ${failWords(err)}`, tone: 'warn' };
}

/** How to reach one person: the link, whether they typed it, whether only the bot's button opens it, what the bot is
 *  asked by (a session id; an imported record's client id), and where they came from. The app is read off the link
 *  itself (wa.me is WhatsApp), so the button can never be named for one app and open the other. */
export interface ChatTarget {
  url: string | null; viaBot: boolean; given: boolean;
  /** The team added it (14 Oct 2026) — said so, never "not verified". */
  byTeam: boolean;
  sessionId: string | null; customerId: string | null; who: string; channel: LeadChannel | null;
}
export const targetOf = (p: { contactUrl: string | null; chatViaBot: boolean; contactGiven: boolean; contactByTeam?: boolean; channel: LeadChannel | null },
  ids: { sessionId: string | null; customerId?: string | null }, who: string): ChatTarget => ({
  url: p.contactUrl, viaBot: p.chatViaBot, given: p.contactGiven && p.contactByTeam !== true, byTeam: p.contactByTeam === true,
  sessionId: ids.sessionId, customerId: ids.customerId ?? null, who, channel: p.channel,
});

/** Each app in its own colour (Telegram blue #2AABEE on dark, WhatsApp green #25D366), with dark words on it; the
 *  word says the app too. A visitor only on the website, a neutral grey. */
const APP = {
  Telegram: { icon: Send, button: 'bg-[#2AABEE] text-[#0B0F14] hover:bg-[#229ED9]', edge: 'border-l-[#2AABEE]', tint: 'bg-[#2AABEE]/[0.07]', ink: 'text-[#2AABEE]' },
  WhatsApp: { icon: MessageCircle, button: 'bg-[#25D366] text-[#0B0F14] hover:brightness-95', edge: 'border-l-[#25D366]', tint: 'bg-[#25D366]/[0.07]', ink: 'text-[#25D366]' },
} as const;
type AppName = keyof typeof APP;
const appOf = (url: string): AppName => (url.startsWith('https://wa.me/') ? 'WhatsApp' : 'Telegram');

/** The chat button's working: a link opens straight away; a Telegram account with no username asks the bot for the
 *  button (changing nothing about the person) and says where it went. */
function useChat(t: ChatTarget) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ text: string; tone: 'plain' | 'warn'; url: string | null } | null>(null);
  const ask = async () => {
    setBusy(true);
    setNote(null);
    try {
      const r = await api.onboardingChatLink(t.sessionId ? { sessionId: t.sessionId } : { customerId: t.customerId! });
      openChat(r.url);
      setNote({ ...chatLinkNote(r), url: r.url });
    } catch (e) {
      setNote({ ...chatLinkNote(null, e), url: null });
    } finally {
      setBusy(false);
    }
  };
  return { busy, note, ask };
}
/** Where the button went, or why nothing was sent — and, when a browser may hold back a tab opened after a wait, the
 *  chat one tap away. */
function ChatNote({ note }: { note: { text: string; tone: 'plain' | 'warn'; url: string | null } | null }) {
  if (!note) return null;
  return (
    <span className={`text-meta ${note.tone === 'warn' ? 'text-warn' : 'text-tg-hint'}`} data-chat-note="">
      {note.text}
      {note.url && <> · <a className="btn-link text-meta" href={note.url} target="_blank" rel="noopener noreferrer"
        onClick={(e) => { e.preventDefault(); openChat(note.url!); }}>Open</a></>}
    </span>
  );
}

/** The chat button on a list row, named and coloured for the app it opens. A contact the visitor typed in the web
 *  chat says "not verified" under it. Nobody to reach outside the bot: "Website only" for a website visitor, else "No
 *  link to their chat". */
export function ChatButton({ t }: { t: ChatTarget }) {
  const chat = useChat(t);
  const btn = 'tap-44 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1 text-body font-semibold';
  if (t.url) {
    const app = appOf(t.url);
    const Icon = APP[app].icon;
    return (
      <span className="inline-flex flex-col items-start gap-0.5" data-chat={app}>
        <a className={`${btn} ${APP[app].button}`} href={t.url} target="_blank" rel="noopener noreferrer" aria-label={`Open the chat with ${t.who} in ${app}`}
          onClick={(e) => { e.preventDefault(); openChat(t.url!); }}>
          <Icon size={14} aria-hidden />{app}
        </a>
        {t.byTeam
          ? <span className="text-meta text-tg-hint" title="The team added this contact for them">added by the team</span>
          : t.given && <span className="text-meta text-tg-hint" title="They typed this in the web chat — not verified">not verified</span>}
      </span>
    );
  }
  if (t.viaBot && (t.sessionId || t.customerId)) {
    return (
      <span className="inline-flex flex-col items-start gap-0.5" data-chat="Telegram">
        <button type="button" className={`${btn} ${APP.Telegram.button} disabled:opacity-60`} disabled={chat.busy}
          aria-label={`Ask the bot for the button to ${t.who}'s Telegram chat`} onClick={() => void chat.ask()}>
          <Send size={14} aria-hidden />{chat.busy ? 'Asking…' : <>Telegram<span className="font-medium opacity-80"> · via bot</span></>}
        </button>
        <ChatNote note={chat.note} />
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md bg-panel-2 px-2.5 py-1 text-body text-tg-hint" data-chat="none">
      <Globe size={14} aria-hidden />{t.channel === 'web' ? 'Website only' : 'No link to their chat'}
    </span>
  );
}

/** The record's one contact card, in its app's colour: who they are on it — read off the link itself, so a WhatsApp
 *  card can never show a Telegram name — and the button that opens it; or, for a Telegram account with no username,
 *  the button the bot sends you; or, with no way to them, why, in grey. */
export function ContactCard({ t }: { t: ChatTarget }) {
  const chat = useChat(t);
  const app: AppName | null = t.url ? appOf(t.url) : t.viaBot && (t.sessionId || t.customerId) ? 'Telegram' : null;
  if (!app) {
    return (
      <div className={`${SEAT} flex items-center gap-3 border-l-4 border-l-brand-muted px-4 py-3`} data-contact="none">
        <Globe size={20} className="shrink-0 text-tg-hint" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-body font-semibold uppercase tracking-wide text-tg-hint">{t.channel === 'web' ? 'Website only' : 'No chat link'}</p>
          <p className="text-body leading-snug">{t.channel === 'web' ? 'No Telegram or WhatsApp given — reply in the website chat' : 'No link to their chat — reply through the bot'}</p>
        </div>
      </div>
    );
  }
  const a = APP[app];
  const Icon = a.icon;
  return (
    <div className={`${SEAT} overflow-hidden border-l-4 ${a.edge}`} data-contact={app}>
      <div className={`flex h-full flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 ${a.tint}`}>
        <Icon size={20} className={`shrink-0 ${a.ink}`} aria-hidden />
        <div className="min-w-0 flex-1">
          <p className={`text-body font-semibold uppercase tracking-wide ${a.ink}`}>{app}</p>
          <p className="break-words text-body leading-snug">
            {t.url ? (app === 'WhatsApp' ? `+${t.url.slice('https://wa.me/'.length)}` : `@${t.url.slice('https://t.me/'.length)}`) : 'No username — the bot sends you the button'}
          </p>
          {t.byTeam
            ? <p className="text-meta text-tg-hint" title="The team added this contact for them">Added by the team</p>
            : t.given && <p className="text-meta text-tg-hint" title="They typed this in the web chat — not verified">Typed in the web chat — not verified</p>}
          <ChatNote note={chat.note} />
        </div>
        {t.url ? (
          <a href={t.url} target="_blank" rel="noopener noreferrer" aria-label={`Open the chat with ${t.who} in ${app}`}
            onClick={(e) => { e.preventDefault(); openChat(t.url!); }} className={`tap-44 shrink-0 rounded-md px-3 py-1.5 text-body font-semibold ${a.button}`}>
            Open chat
          </a>
        ) : (
          <button type="button" disabled={chat.busy} aria-label={`Ask the bot for the button to ${t.who}'s Telegram chat`} onClick={() => void chat.ask()}
            className={`tap-44 shrink-0 rounded-md px-3 py-1.5 text-body font-semibold disabled:opacity-60 ${a.button}`}>
            {chat.busy ? 'Asking…' : 'Get the button'}
          </button>
        )}
      </div>
    </div>
  );
}

/** A link to a card in the ops group — opened inside Telegram from the Mini App. */
export function CardButton({ url, says = 'Card' }: { url: string; says?: string }) {
  return (
    <a className="btn-link whitespace-nowrap text-body" href={url} target="_blank" rel="noopener noreferrer" aria-label="Open the card in the ops group"
      onClick={(e) => { e.preventDefault(); openChat(url); }}>
      {says}
    </a>
  );
}

/** One click out of the page — the wallet on its chain's explorer, or on Arkham — in a new tab with no hold on this one. */
export function OutButton({ href, label }: { href: string | null; label: string }) {
  if (!href) return null;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => { e.preventDefault(); openChat(href); }}
      className="tap-44 inline-flex items-center gap-1 rounded-md border border-stroke bg-panel-2 px-3 py-1.5 text-body font-medium hover:border-brand-muted">
      {label}<ArrowUpRight size={14} aria-hidden />
    </a>
  );
}

/** A section's frame, the same for every section: a card with a clear head — its title and its verdicts together, and
 *  on the right a slot for its actions (the Wallet section's Transactions and Arkham; later an "Ask the customer"
 *  button), there empty or not, so an action drops in without a redesign — then, under the head, a slot for a short
 *  list of requests to the customer (who asked, when it was sent, the answer: a later plan), then its body. */
export function Section({ id, title, verdicts, aside, requests, children }: {
  id: string; title: string; verdicts?: Array<{ s: CheckStatus; of?: string }>; aside?: ReactNode; requests?: ReactNode; children: ReactNode;
}) {
  return (
    <section id={id} className="card scroll-mt-4 p-0" aria-label={title}>
      <header className="flex flex-col gap-2.5 border-b border-stroke-soft px-5 py-3.5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <h3 className="text-title font-semibold">{title}</h3>
          {verdicts && verdicts.length > 0 && <span className="flex flex-wrap items-center gap-2">{verdicts.map((x, i) => <StatusPill key={i} s={x.s} of={x.of} />)}</span>}
        </div>
        <div className="flex flex-wrap gap-2 empty:hidden" data-actions="">{aside}</div>
      </header>
      {requests && <div className="border-b border-stroke-soft px-5 py-3" data-requests="">{requests}</div>}
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

/** A labelled line of a section: every label in one column, every value on one left edge, a long value wrapping under
 *  itself at about 70 characters a line. */
export function Line({ k, children }: { k: string; children: ReactNode }) {
  return (
    <>
      <dt className="pt-1 text-label font-medium uppercase tracking-wide text-tg-hint">{k}</dt>
      <dd className="min-w-0 max-w-[64ch] text-pretty text-body leading-relaxed" data-line={k}>{children}</dd>
    </>
  );
}
export function Lines({ children }: { children: ReactNode }) {
  return <dl className="grid grid-cols-[9.5rem_minmax(0,1fr)] gap-x-4 gap-y-2.5 max-sm:grid-cols-1 max-sm:gap-y-1">{children}</dl>;
}

/** A long value: three lines, the rest one tap away — no run-on paragraph. */
export function Clip({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const long = text.length > 210;
  return (
    <span>
      <span className={long && !open ? 'line-clamp-3' : ''} title={long ? text : undefined}>{text}</span>
      {long && <button type="button" className="btn-link text-body" onClick={() => setOpen(!open)}>{open ? 'less' : 'more'}</button>}
    </span>
  );
}
